import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { TitleCasePipe } from '@angular/common';
import { Router } from '@angular/router';
import { Button } from 'primeng/button';
import { distinctUntilChanged, firstValueFrom, map } from 'rxjs';
import { FeatureFlagKey, FeatureFlagsService } from '@brighthub/feature-flags';
import { ClientSearchService } from '@brightnav/ui';
import {
  Activity,
  actionCountDigit,
  actionPreviewTitles,
  triggeringQuote,
} from '../../models/activity.model';
import { ActivityInboxService, describeHttpError } from '../../services/activity-inbox.service';
import { ActivityDetailDialogComponent } from '../activity-detail-dialog/activity-detail-dialog.component';

function isFlagOn(value: unknown): boolean {
  return value !== false && value !== null && value !== undefined;
}

interface ClientDisplay {
  name: string;
  customer: string;
}

/**
 * Compact org-wide confirmation queue for Home. Does not mount `bn-review-queue`
 * (that requires a client GUID).
 */
@Component({
  selector: 'bn-review-dashboard-widget',
  standalone: true,
  imports: [Button, ActivityDetailDialogComponent],
  templateUrl: './review-dashboard-widget.component.html',
  styleUrl: './review-dashboard-widget.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReviewDashboardWidgetComponent {
  private readonly _inbox = inject(ActivityInboxService);
  private readonly _flags = inject(FeatureFlagsService);
  private readonly _clientsApi = inject(ClientSearchService);
  private readonly _router = inject(Router);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _titleCase = new TitleCasePipe();
  private _fetchEnabled = false;
  private _reloadGen = 0;

  readonly reviewEnabled = toSignal(
    this._flags
      .getValueChanges$(FeatureFlagKey.ENABLEAIREVIEW, false)
      .pipe(map((value) => isFlagOn(value))),
    { initialValue: false },
  );
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly activities = signal<Activity[]>([]);
  readonly selectedActivity = signal<Activity | null>(null);
  readonly detailVisible = signal(false);
  readonly clients = signal<Record<string, ClientDisplay>>({});

  constructor() {
    this._flags
      .getValueChanges$(FeatureFlagKey.ENABLEAIREVIEW, false)
      .pipe(
        map((value) => isFlagOn(value)),
        distinctUntilChanged(),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe((enabled) => {
        this._fetchEnabled = enabled;
        if (enabled) {
          void this.reload();
          return;
        }
        this._reloadGen += 1;
        this.activities.set([]);
        this.loading.set(false);
        this.error.set(null);
      });
  }

  async reload(): Promise<void> {
    if (!this._fetchEnabled) {
      return;
    }
    const gen = ++this._reloadGen;
    this.loading.set(true);
    this.error.set(null);
    try {
      const page = await this._inbox.loadConfirmationQueue();
      if (gen !== this._reloadGen) {
        return;
      }
      this.activities.set(page.items);
      void this._resolveClients(page.items, gen);
    } catch (err) {
      if (gen !== this._reloadGen) {
        return;
      }
      this.error.set(describeHttpError(err));
      this.activities.set([]);
    } finally {
      if (gen === this._reloadGen) {
        this.loading.set(false);
      }
    }
  }

  open(activity: Activity): void {
    this.selectedActivity.set(activity);
    this.detailVisible.set(true);
  }

  openFromLink(event: Event, activity: Activity): void {
    event.stopPropagation();
    this.open(activity);
  }

  openClient(event: Event, activity: Activity): void {
    event.stopPropagation();
    const guid = this.clientGuid(activity);
    if (!guid) {
      return;
    }
    void this._router.navigate(['/brightnav/myclients'], {
      queryParams: { guid },
    });
  }

  async onActivityChanged(): Promise<void> {
    await this.reload();
  }

  proposedActivity(activity: Activity): string {
    return (
      actionPreviewTitles(activity)?.[0] ||
      activity.router_proposal.title ||
      activity.title
    );
  }

  clientSaid(activity: Activity): string {
    return triggeringQuote(activity) ?? '—';
  }

  actionCount(activity: Activity): string {
    const count = actionCountDigit(activity);
    return `${count} ${count === '1' ? 'action' : 'actions'}`;
  }

  clientGuid(activity: Activity): string | null {
    const guid = activity.client_guid?.trim();
    return guid || null;
  }

  clientName(activity: Activity): string {
    const guid = this.clientGuid(activity);
    if (!guid) {
      return '—';
    }
    return this.clients()[guid]?.name || '—';
  }

  /** Title-cased customer for the Home table, or an em dash when unknown. */
  clientCustomer(activity: Activity): string {
    const guid = this.clientGuid(activity);
    if (!guid) {
      return '—';
    }
    const raw = this.clients()[guid]?.customer?.trim();
    if (!raw || raw === '—') {
      return '—';
    }
    return this._titleCase.transform(raw) ?? raw;
  }

  receivedLabel(activity: Activity, now = new Date()): string {
    const then = new Date(activity.created_at).getTime();
    if (Number.isNaN(then)) {
      return '—';
    }
    const minutes = Math.max(0, Math.round((now.getTime() - then) / 60_000));
    if (minutes < 1) {
      return 'Just now';
    }
    if (minutes < 60) {
      return `${minutes} min ago`;
    }
    const hours = Math.round(minutes / 60);
    if (hours < 24) {
      return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
    }
    const days = Math.round(hours / 24);
    return `${days} ${days === 1 ? 'day' : 'days'} ago`;
  }

  private async _resolveClients(
    activities: Activity[],
    gen: number,
  ): Promise<void> {
    const guids = [
      ...new Set(
        activities
          .map((item) => item.client_guid?.trim())
          .filter((guid): guid is string => !!guid),
      ),
    ];
    const known = this.clients();
    const missing = guids.filter((guid) => !known[guid]);
    if (!missing.length) {
      return;
    }

    const resolved = await Promise.all(
      missing.map(async (guid) => {
        try {
          const response = await firstValueFrom(
            this._clientsApi.searchClients(guid),
          );
          const match = response.payload?.clients?.find(
            (item) => item.guid?.trim().toLowerCase() === guid.toLowerCase(),
          );
          if (!match) {
            return null;
          }
          const name = [match.firstName, match.lastName]
            .filter(Boolean)
            .join(' ')
            .trim();
          return [
            guid,
            {
              name: name || '—',
              customer: match.customer?.trim() || '—',
            },
          ] as const;
        } catch {
          return null;
        }
      }),
    );

    if (gen !== this._reloadGen) {
      return;
    }
    this.clients.update((map) => {
      const next = { ...map };
      for (const row of resolved) {
        if (row) {
          next[row[0]] = row[1];
        }
      }
      return next;
    });
  }
}
